import { CustomerServiceGateway } from '../customer-service';
import { ChatConfigGateway } from '../chat-config';
import { CampaignsGateway } from '../campaigns';
import { ChatAdapterGateway } from '../chat-adapter';
import { AssetsGateway } from '../assets';
import { ContactListGateway } from '../contact-list';
import { IntegrationsGateway } from '../integrations';
import { ChatWebserviceGateway } from '../chat-webservice';
import { CallReportGateway } from '../call-report';
import { CallsGateway } from '../calls';
import { CasesGateway } from '../cases';
import { RhGateway } from '../rh';
import { AccessHubGateway } from '../access-hub';
import { NotificationsGateway } from '../notifications';
import axios, { AxiosInstance } from 'axios';

/**
 * Maps service identifiers to their corresponding gateway types.
 * Used by the `gateway()` function to infer the correct return type
 * based on the service name passed as argument.
 *
 * When adding a new service, add an entry here with the service
 * identifier as the key and the gateway class as the value.
 */
export type GatewayMap = {
  'customer-service': CustomerServiceGateway;
  'chat-config': ChatConfigGateway;
  campaigns: CampaignsGateway;
  'chat-adapter': ChatAdapterGateway;
  assets: AssetsGateway;
  'contact-list': ContactListGateway;
  integrations: IntegrationsGateway;
  'chat-webservice': ChatWebserviceGateway;
  'call-report': CallReportGateway;
  calls: CallsGateway;
  cases: CasesGateway;
  rh: RhGateway;
  'access-hub': AccessHubGateway;
  notifications: NotificationsGateway;
};

export interface SDKGatewaysInterface {
  customerService: CustomerServiceGateway | null;
  chatConfig: ChatConfigGateway | null;
  campaigns: CampaignsGateway | null;
  chatAdapter: ChatAdapterGateway | null;
  assets: AssetsGateway | null;
  contactList: ContactListGateway | null;
  integrations: IntegrationsGateway | null;
  chatWebservice: ChatWebserviceGateway | null;
  callReport: CallReportGateway | null;
  calls: CallsGateway | null;
  cases: CasesGateway | null;
  rh: RhGateway | null;
  accessHub: AccessHubGateway | null;
  notifications: NotificationsGateway | null;
}

class SDKGateways {
  customerService: CustomerServiceGateway | null;
  chatConfig: ChatConfigGateway | null;
  campaigns: CampaignsGateway | null;
  chatAdapter: ChatAdapterGateway | null;
  assets: AssetsGateway | null;
  contactList: ContactListGateway | null;
  integrations: IntegrationsGateway | null;
  chatWebservice: ChatWebserviceGateway | null;
  callReport: CallReportGateway | null;
  calls: CallsGateway | null;
  cases: CasesGateway | null;
  rh: RhGateway | null;
  accessHub: AccessHubGateway | null;
  notifications: NotificationsGateway | null;
  constructor(
    customerService: CustomerServiceGateway | null,
    chatConfig: ChatConfigGateway | null,
    campaigns: CampaignsGateway | null,
    chatAdapter: ChatAdapterGateway | null,
    assets: AssetsGateway | null,
    contactList: ContactListGateway | null,
    integrations: IntegrationsGateway | null,
    chatWebservice: ChatWebserviceGateway | null,
    callReport: CallReportGateway | null,
    calls: CallsGateway | null,
    cases: CasesGateway | null,
    rh: RhGateway | null,
    accessHub: AccessHubGateway | null,
    notifications: NotificationsGateway | null,
  ) {
    this.customerService = customerService;
    this.chatConfig = chatConfig;
    this.campaigns = campaigns;
    this.chatAdapter = chatAdapter;
    this.assets = assets;
    this.contactList = contactList;
    this.integrations = integrations;
    this.chatWebservice = chatWebservice;
    this.callReport = callReport;
    this.calls = calls;
    this.cases = cases;
    this.rh = rh;
    this.accessHub = accessHub;
    this.notifications = notifications;
  }
}

/**
 * Parameters required to configure the SDK client.
 * Each service configuration is optional, only provide the services you need.
 */
export interface GatewayClientParams {
  /** An Axios instance used as the HTTP client for all gateway requests (optional). */
  axiosClient?: AxiosInstance;
  /** API token used for authentication across all services. */
  apiToken: string;
  /** Configuration for each service gateway. Only the services you configure will be available. */
  services: {
    customerService?: {
      baseUrl: string;
    };
    chatConfig?: {
      baseUrl: string;
    };
    campaigns?: {
      baseUrl: string;
    };
    chatAdapter?: {
      baseUrl: string;
    };
    assets?: {
      baseUrl: string;
    };
    contactList?: {
      baseUrl: string;
    };
    integrations?: {
      baseUrl: string;
    };
    chatWebservice?: {
      baseUrl: string;
    };
    callReport?: {
      baseUrl: string;
    };
    calls?: {
      baseUrl: string;
    };
    cases?: {
      baseUrl: string;
    };
    rh?: {
      baseUrl: string;
    };
    accessHub?: {
      baseUrl: string;
    };
    notifications?: {
      baseUrl: string;
    };
  };
}

/**
 * Creates an SDKGateways instance with all service gateways initialized
 * using the provided configuration parameters.
 * Services without configuration will be set to null and will throw
 * an error if accessed via `gateway()`.
 */
export function createClient(params: GatewayClientParams) {
  const httpClient =
    params.axiosClient ||
    axios.create({
      headers: {
        Authorization: `Bearer ${params.apiToken}`,
      },
    });
  const customerService = params.services.customerService
    ? new CustomerServiceGateway(httpClient, params.services.customerService.baseUrl)
    : null;
  const chatConfig = params.services.chatConfig
    ? new ChatConfigGateway(httpClient, params.services.chatConfig.baseUrl)
    : null;
  const campaigns = params.services.campaigns
    ? new CampaignsGateway(httpClient, params.services.campaigns.baseUrl)
    : null;
  const chatAdapter = params.services.chatAdapter
    ? new ChatAdapterGateway(httpClient, params.services.chatAdapter.baseUrl)
    : null;
  const assets = params.services.assets ? new AssetsGateway(httpClient, params.services.assets.baseUrl) : null;
  const contactList = params.services.contactList
    ? new ContactListGateway(httpClient, params.services.contactList.baseUrl)
    : null;
  const integrations = params.services.integrations
    ? new IntegrationsGateway(httpClient, params.services.integrations.baseUrl)
    : null;
  const chatWebservice = params.services.chatWebservice
    ? new ChatWebserviceGateway(httpClient, params.services.chatWebservice.baseUrl)
    : null;
  const callReport = params.services.callReport
    ? new CallReportGateway(httpClient, params.services.callReport.baseUrl)
    : null;
  const calls = params.services.calls ? new CallsGateway(httpClient, params.services.calls.baseUrl) : null;
  const cases = params.services.cases ? new CasesGateway(httpClient, params.services.cases.baseUrl) : null;
  const rh = params.services.rh ? new RhGateway(httpClient, params.services.rh.baseUrl) : null;
  const accessHub = params.services.accessHub
    ? new AccessHubGateway(httpClient, params.services.accessHub.baseUrl)
    : null;
  const notifications = params.services.notifications
    ? new NotificationsGateway(httpClient, params.services.notifications.baseUrl)
    : null;
  return new SDKGateways(
    customerService,
    chatConfig,
    campaigns,
    chatAdapter,
    assets,
    contactList,
    integrations,
    chatWebservice,
    callReport,
    calls,
    cases,
    rh,
    accessHub,
    notifications,
  );
}
